/**
 * **Field ＋ カメラ → 舞台のピクセル**（Issue #522）。
 *
 * Field は盤の上の座標（平面のピクセル）しか答えない。舞台のどこに写るかは**カメラ次第＝
 * そのフレーム次第**なので、Field に持たせず、ここで「今のカメラでの見え方」を作って捨てる
 * （`docs/fx-camera-design.md` §4。Field を毎フレーム作り直すと舞台の同一性が壊れる）。
 *
 * 出す口の名前（`toStage` / `zoneQuad` / `outline` / `known`）は旧演出プレビュー（Issue #639 で削除）の演出が
 * 読んでいた名前に合わせてある。
 *
 * **カード用の口（旧 `cardQuad`）は無い**（Issue #523）—— 盤に置かれているものはオブジェクトの層が
 * 描き、**ゾーンのキー**で引く。守備表示のように回すものは `zonePlane()`（平面の 4 点）を取って
 * 平面の座標で回してから `toStage` する（舞台のピクセルで回すと遠近が付いた後の形を回すことになる）。
 */
import { project, unproject } from './camera';

/** 4 点のうち 1 つでも写らない（カメラの後ろ）なら null */
const projectQuad = (quad, toStage) => {
  if (!quad) return null;
  const out = quad.map(toStage);
  return out.every((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1])) ? out : null;
};

/**
 * @param field `fields/` の Field
 * @param shot カメラのショット
 * @param canvas 舞台の大きさ
 */
export function stageView(field, shot, canvas = { w: 1920, h: 1080 }) {
  const plane = field.plane;
  const fwd = project(shot, plane, canvas);
  const inv = unproject(shot, plane, canvas);
  const toStage = (pt) => fwd(pt);
  const zoneQuad = (key) => projectQuad(field.zonePlane(key), toStage);
  const outline = projectQuad(field.planeCorners(), toStage);
  return {
    known: field.known,
    virtual: field.virtual,
    plane,
    unit: field.unit,
    keys: field.keys,
    /** このフレームのカメラ（= three のカメラの数値そのもの）と舞台の大きさ。**描く側が three に渡す** */
    shot,
    canvas,
    /** 平面の座標 → 舞台のピクセル（カメラの後ろは null） */
    toStage,
    /** 舞台のピクセル → 平面の座標（地平線の向こうは `[NaN, NaN]`） */
    toPlane: inv,
    /** ゾーンの枠の**平面の 4 点**（回してから投影したいとき） */
    zonePlane: (key) => field.zonePlane(key),
    /** そのゾーンに置かれたカードの向き（平面の中での回し。ラジアン） */
    cardAngle: (key) => field.cardAngle(key),
    zoneQuad,
    /** 盤（平面）の写っている 4 点。潰れていれば null */
    outline,
  };
}
